/* eslint-disable */
/**
 * SubmitEvent — Member-facing event submission page.
 *
 * Session 60.6 · Members submit local events for admin review so Commons
 * doesn't depend on Cam personally creating every listing. The form uses
 * the same split date/time UX as AdminEvents (P0.2 fix from Session 60.5)
 * so times never shift between save + reload.
 *
 * Every submission lands in `db.aggregated_events` with `status='pending'`
 * and requires admin ✓ before appearing on /events — no spam risk.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";

const CATEGORIES = [
  { key: "community",   label: "Community & networking" },
  { key: "workshop",    label: "Workshop / class" },
  { key: "social",      label: "Social" },
  { key: "wellness",    label: "Wellness" },
  { key: "founder",     label: "Founder / startup" },
  { key: "arts",        label: "Arts & culture" },
  { key: "food",        label: "Food & drink" },
  { key: "other",       label: "Other" },
];

function localPartsToIso(dateStr, timeStr) {
  if (!dateStr) return "";
  const t = /^\d{1,2}:\d{2}$/.test(timeStr || "") ? timeStr : "18:00";
  const local = new Date(`${dateStr}T${t}`);
  return Number.isNaN(local.getTime()) ? "" : local.toISOString();
}

export default function SubmitEvent() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", city: "", category: "community",
    venue: "", start_date: "", start_time: "18:00", end_date: "", end_time: "20:00",
    registration_url: "", image_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const canSubmit = form.title.trim().length > 2 && form.city.trim().length > 1 && form.start_date;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        city: form.city.trim(),
        category: form.category,
        venue: form.venue.trim(),
        start_at: localPartsToIso(form.start_date, form.start_time),
        end_at: form.end_date ? localPartsToIso(form.end_date, form.end_time) : null,
        registration_url: form.registration_url.trim() || null,
        image_url: form.image_url.trim() || null,
      };
      await api.post("/events/submit", payload);
      toast.success("Submitted for review. We'll email you when it's approved.");
      setTimeout(() => nav("/events"), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="m-page-pad" style={{ maxWidth: 820, margin: "0 auto" }}>
        <span className="m-eyebrow">Submit an Event</span>
        <h1 className="m-h1">Share what's <em>happening</em>.</h1>
        <p className="m-lede" style={{ marginBottom: 24 }}>
          Know about a great local event? Submit it here. Métier reviews every
          submission and publishes to the community events feed once approved.
        </p>

        {!user && (
          <div className="m-card" style={{ padding: 20, marginBottom: 20, background: "rgba(184,145,58,0.08)" }}>
            You'll need to <Link to="/login" style={{color:"var(--gold)"}}>sign in</Link> before submitting.
          </div>
        )}

        <div data-testid="submit-event-form" className="m-card" style={{ padding: "26px 28px", display: "grid", gap: 16 }}>
          <label className="m-field">
            <span>Event title</span>
            <input value={form.title} onChange={update("title")}
              placeholder="e.g. Founder Friday Coffee @ Little Amps"
              data-testid="submit-event-title" />
          </label>
          <label className="m-field">
            <span>Description</span>
            <textarea value={form.description} onChange={update("description")} rows={4}
              placeholder="What's it about? Who is it for?"
              data-testid="submit-event-desc" />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <label className="m-field">
              <span>City</span>
              <input value={form.city} onChange={update("city")}
                placeholder="Harrisburg" data-testid="submit-event-city" />
            </label>
            <label className="m-field">
              <span>Category</span>
              <select value={form.category} onChange={update("category")} data-testid="submit-event-category">
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="m-field">
            <span>Venue (optional)</span>
            <input value={form.venue} onChange={update("venue")}
              placeholder="Little Amps Coffee Roasters"
              data-testid="submit-event-venue" />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <label className="m-field">
              <span>Start date</span>
              <input type="date" value={form.start_date} onChange={update("start_date")}
                data-testid="submit-event-start-date" />
            </label>
            <label className="m-field">
              <span>Start time (your local)</span>
              <input type="time" value={form.start_time} onChange={update("start_time")}
                data-testid="submit-event-start-time" />
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <label className="m-field">
              <span>End date (optional, multi-day)</span>
              <input type="date" value={form.end_date} onChange={update("end_date")}
                data-testid="submit-event-end-date" />
            </label>
            <label className="m-field">
              <span>End time (your local)</span>
              <input type="time" value={form.end_time} onChange={update("end_time")}
                data-testid="submit-event-end-time" />
            </label>
          </div>
          <label className="m-field">
            <span>Registration link (optional)</span>
            <input value={form.registration_url} onChange={update("registration_url")}
              placeholder="https://eventbrite.com/e/…"
              data-testid="submit-event-reg-url" />
          </label>
          <label className="m-field">
            <span>Event image URL (optional)</span>
            <input value={form.image_url} onChange={update("image_url")}
              placeholder="https://…"
              data-testid="submit-event-image-url" />
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit || submitting || !user}
              className="m-btn-gold"
              data-testid="submit-event-btn"
              style={{ opacity: canSubmit && user && !submitting ? 1 : 0.55 }}
            >
              {submitting ? "Submitting..." : "Submit for review"}
            </button>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Métier reviews each submission before publishing.
            </span>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
